import React, { useContext, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { Formik, Form } from "formik";
import UserContext from "../../context/user/UserContext";
import { Flex, Box } from "reflexbox";
import LineTable from "./PrintQuote/LineTable";
import Card from "../UI/Card";
import Button from "../UI/Button";
import Field from "../UI/forms/Field";
import PrintQuote from "./PrintQuote";
import Checkout from "./Checkout";
import {
  updateQuote,
  placeOrder,
} from "../../requests/quoteManagementRequests";
import { useTheme } from "styled-components";
import Loading from "../UI/Loading";
import PagerContextProvider from "../UI/Pager/PagerContext";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import AlertContext from "../../context/alert/AlertContext";
import { Body } from "../UI/Text";
import { formatPhoneNumber } from "../../modules/utilities";
import AddressHeader from "./Customer/AddressHeader";

const Summary = ({
  lineCount,
  printRef,
  preview,
  setPreview,
  communications,
  refetchQuote,
  lineLoading,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeUser, platform } = useContext(UserContext);
  const { showFailureAlert } = useContext(AlertContext);
  const { quote, setQuote } = useContext(ConfiguratorContext);
  const { integration } = platform || {};

  const { id, _id, lines, jobName, specifier, specifierLocation } = quote || {}; //poNumber, 

  const [isLoading, setLoading] = useState(false);

  const isCheckout = activeUser?.checkoutType === "stripe";

  const onSubmit = useCallback(
    async ({ jobName: _jobName, specifier: _specifier, specifierLocation: _specifierLocation }) => {
      setQuote({
        ...quote,
        jobName: _jobName,
        specifier: _specifier,
        specifierLocation: _specifierLocation,
      });
      await updateQuote({
        input: { jobName: _jobName, specifier: _specifier, specifierLocation: _specifierLocation },
        _id: _id,
        id,
      });
    },
    [_id, id, quote, setQuote]
  );

  const onPlaceOrder = useCallback(
    async ({ jobName, specifier, specifierLocation }) => {
      if (quote?.lines?.length < 1) {
        return showFailureAlert(
          "You must have at least one Quote Line to place an order."
        );
      }
      if (!quote?.customer?.erpId && activeUser?.checkoutType !== "stripe") {
        return showFailureAlert(
          "An ERP ID is required to submit quotes to SyteLine"
        );
      }
      if (!jobName) {
        return showFailureAlert(
          "You must provide a job name on the Quote Summary before placing an Order."
        );
      }

      if (!specifier) {
        return showFailureAlert(
          "You must provide a specifier on the Quote Summary before placing an Order."
        );
      }

      if (!specifierLocation) {
        return showFailureAlert(
          "You must provide a specifier location on the Quote Summary before placing an Order."
        );
      }
      // if (!poNumber) {
      //   return showFailureAlert(
      //     "You must provide a Customer PO Number on the Quote Summary before placing an Order."
      //   );
      // }
      setLoading(true);
      await updateQuote({
        input: { jobName, specifier, specifierLocation },
        _id: _id,
        id,
      });

      await placeOrder({
        type: "order",
        input: {
          type: 2,
          integration,
        },
        _id: _id || undefined,
        id,
      });
      refetchQuote("myOrders");
      refetchQuote("quotes");
      refetchQuote("quoteRequests");

      navigate("/ThankYou");
      setLoading(false);
    },
    [
      _id,
      id,
      integration,
      navigate,
      refetchQuote,
      quote,
      activeUser,
      showFailureAlert,
    ]
  );

  const { companyName } = useMemo(() => quote?.customer || {}, [quote]);
  const { firstName, lastName, email, phone } = useMemo(
    () => quote?.contact || {},
    [quote]
  );
  const { Address, Address2, City, State, Zip } = useMemo(
    () => quote?.billingAddress || {},
    [quote]
  );

  return (
    <>
      <PagerContextProvider>
        <PrintQuote
          ref={printRef}
          lines={lines}
          lineCount={lineCount}
          preview={preview}
          setPreview={setPreview}
          communications={communications}
          lineLoading={lineLoading}
        />
      </PagerContextProvider>
      <AddressHeader
        setEditingId={null}
        setFormToShow={null}
        shippingColor={null}
        hideEdit={true}
      />
      <Box mb="1rem">
        <Flex
          alignItems={["center", "center", "center", "flex-start"]}
          flexDirection={["column", "column", "column", "row"]}
          style={{ gap: "1rem" }}
        >
          <Box flexDirection="column" mt="-2rem" flex={2} width={1}>
            <PagerContextProvider>
              <LineTable
                open={true}
                lineLoading={lineLoading}
                lines={lines}
                lineCount={lineCount}
                quoteId={id}
                quote={quote}
              />
            </PagerContextProvider>
            {/* <CommunicationList communications={communications} /> */}
          </Box>
          {quote?.type !== 1 ? null : (
            <Flex mt={["1rem", "1rem", "1rem", 0]} flex={1}>
              {/**
               * Add PO number and account number fields instead of checkout
               */}
              {isCheckout ? (
                <Checkout lines={lines} quote={quote} activeUser={activeUser} />
              ) : (
                <Card width={1}>
                  <Flex
                    width={1}
                    flexDirection="column"
                    alignItems="center"
                    mx="1rem"
                  >
                    <Formik
                      onSubmit={onPlaceOrder}
                      enableReinitialize
                      initialValues={{                      
                        jobName: jobName || "",
                        specifier: specifier || "",
                        specifierLocation: specifierLocation || "",
                      }}
                      validateOnMount={false}
                      validateOnChange={false}
                      validationSchema={yup.object().shape({                       
                        jobName: yup
                          .string("Job Name is required.")
                          .required("Job Name is required."),
                        specifier: yup
                          .string("Specifier is required.")
                          .required("Specifier is required."),
                        specifierLocation: yup
                          .string("Specifer Location is required.")
                          .required("Specifer Location is required."),
                      })}
                    >
                      {({ values }) => {
                        return (
                          <Form style={{ width: "100%" }}>
                            <Flex width={1} flexDirection="column">
                              {!companyName ? null : (
                                <Flex
                                  mb="1rem"
                                  width={1}
                                  justifyContent="space-between"
                                >
                                  <Body bold>Company:</Body>

                                  <Body right color={theme.secondary}>
                                    {companyName}
                                  </Body>
                                </Flex>
                              )}
                            </Flex>
                            {/* <Field
                              required
                              disabled={quote?.type === 2}
                              labelText="PO Number: "
                              onBlur={() => onSubmit(values)}
                              name="poNumber"
                              maxLength={25}
                            /> */}
                            <Field
                              required                              
                              labelText="Job Name: "
                              onBlur={() => onSubmit(values)}
                              name="jobName"
                              maxLength={50}
                            />
                            <Field
                              required                              
                              labelText="Specifier: "
                              onBlur={() => onSubmit(values)}
                              name="specifier"
                              maxLength={50}
                            />
                            <Field
                              required                              
                              labelText="Specifier Location: "
                              onBlur={() => onSubmit(values)}
                              name="specifierLocation"
                              maxLength={50}
                            />
                            {/* <Flex w={1} justifyContent="center">
                              <Button
                                mt="1.5rem"
                                mx="auto"
                                iconName="faFileInvoiceDollar"
                                type="submit"
                              >
                                {isLoading ? (
                                  <Loading
                                    inline
                                    color1={theme.secondary}
                                    color2={theme.success}
                                  />
                                ) : (
                                  "Place Order"
                                )}
                              </Button>
                            </Flex> */}
                          </Form>
                        );
                      }}
                    </Formik>
                  </Flex>
                </Card>
              )}
            </Flex>
          )}
          {/* This will be for place order when user is not assigned to self checkout  */}
        </Flex>
      </Box>
    </>
  );
};

export default Summary;
